import { NavLink, useSearchParams } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { RoleBadge } from '@/auth/RoleBadge';
import { ThemeAccentPicker, ThemeModeToggle } from '@/theme/theme-controls';
import { VerseSelector } from '@/verse/VerseSelector';
import { GatewaySelector } from '@/verse/GatewaySelector';
import { VerseManualInput } from '@/verse/VerseManualInput';

interface NavItem {
  to: string;
  label: string;
  danger?: boolean;
}
interface NavGroup {
  heading?: string;
  items: NavItem[];
  danger?: boolean;
}

/**
 * 모든 verse 에서 노출되는 고정 네비 그룹.
 */
export function buildNavGroups(): NavGroup[] {
  return [
    {
      heading: 'Overview',
      items: [{ to: '/dashboard', label: 'Dashboard' }],
    },
    {
      heading: 'User Data',
      items: [
        { to: '/users', label: 'Users' },
        { to: '/global-state', label: 'Global State' },
        { to: '/query-console', label: 'Query Console' },
        { to: '/migration', label: '계정 이전 원장' },
      ],
    },
    {
      heading: '통계',
      items: [
        { to: '/funnel', label: '스테이지 퍼널' },
        { to: '/shop/revenue', label: '결제 매출' },
        { to: '/shop/glory', label: '글로리샵 분석' },
        { to: '/degen/prize-log', label: '디젠 상금 원장' },
      ],
    },
    {
      heading: 'Master Data',
      items: [
        { to: '/master-data', label: 'Editor' },
        { to: '/master-data/manifest', label: 'JSON Result' },
        { to: '/master-data/sql', label: 'SQL Query' },
        { to: '/master-data/enums', label: 'Enums' },
        { to: '/master-data/seeder', label: 'Seeder' },
      ],
    },
    {
      heading: 'Assets',
      items: [{ to: '/assets', label: 'Asset Viewer' }],
    },
    {
      heading: 'Operations',
      items: [
        { to: '/maintenance', label: 'Maintenance Mode' },
        { to: '/mail-send', label: '메일 전송' },
      ],
    },
    {
      heading: 'Rankings',
      items: [
        { to: '/rankings', label: 'Season Rankings' },
        { to: '/rankings/validation', label: 'Validation Status' },
      ],
    },
    {
      heading: 'Events',
      items: [
        { to: '/event-management', label: '이벤트 관리' },
        { to: '/notice-management', label: '공지 관리' },
      ],
    },
  ];
}

export function Sidebar() {
  const [searchParams] = useSearchParams();
  const verseQuery = searchParams.get('verse');
  const groups = buildNavGroups();

  return (
    <aside className="flex h-full w-64 flex-col gap-3 border-r border-sidebar-border bg-sidebar p-4 text-sidebar-foreground">
      <div className="flex items-center justify-between gap-2">
        <span className="text-lg font-bold tracking-wider text-primary">RB Admin</span>
        <div className="flex items-center gap-1">
          <ThemeAccentPicker />
          <ThemeModeToggle />
        </div>
      </div>
      <GatewaySelector />
      <VerseSelector />
      <VerseManualInput />
      <Separator />
      <ScrollArea className="flex-1 -mx-2">
        <nav className="flex flex-col gap-3 px-2 text-sm">
          {groups.map((g) => (
            <div key={g.heading ?? g.items[0]?.to} className="flex flex-col gap-1">
              {g.heading && (
                <div
                  className={cn(
                    'px-2 text-xs font-bold uppercase tracking-wider',
                    g.danger ? 'text-destructive' : 'text-muted-foreground'
                  )}
                >
                  {g.heading}
                </div>
              )}
              {g.items.map((it) => {
                const isDanger = g.danger || it.danger;
                return (
                  <NavLink
                    key={it.to}
                    to={verseQuery ? `${it.to}?verse=${verseQuery}` : it.to}
                    className={({ isActive }) =>
                      cn(
                        'rounded px-3 py-1.5 transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : isDanger
                            ? 'text-destructive/80 hover:bg-destructive/10 hover:text-destructive'
                            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
                      )
                    }
                  >
                    {it.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </ScrollArea>
      <Separator />
      <RoleBadge />
    </aside>
  );
}
